const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { GifPicker } = require('gif-picker-react');
const { Klipy } = require('gif-picker-react/providers/klipy');
const { runProfanityCheck } = require('utils/profanityFilter');

const KLIPY_PLACEHOLDER_APP_KEY = 'KLIPY_PROXY';
const KLIPY_PROXY_BASE_URL = `${process.env.API_HOST}/api/klipy/`;

const internals = {};

const { useEffect, useMemo, useRef } = React;

function KlipyGifPicker(props) {

    const { SafeGifPicker } = internals;

    return (
        <SafeGifPicker
            {...props}
        />
    );
}

module.exports = KlipyGifPicker;

internals.GifPickerWrapper = Styled.div`
    position: relative;
`;

internals.SafeGifPicker = function SafeGifPicker(props) {

    const {
        onGifClick,
        useProfanityFilter,
        setBadWordsDialogOpen,
        setBadWordList,
        contentFilter,
        ...otherProps
    } = props;

    const { GifPickerWrapper } = internals;
    const pickerRef = useRef(null);

    const provider = useMemo(() => Klipy(KLIPY_PLACEHOLDER_APP_KEY, {
        baseUrl: KLIPY_PROXY_BASE_URL,
        contentFilter
    }), [contentFilter]);

    useEffect(() => {

        const findSearchInput = () => {

            const selectors = [
                'input[type="search"]',
                'input[placeholder*="Search"]',
                'input.gpr-search',
                '.GifPickerReact input',
                'input'
            ];

            for (const selector of selectors) {
                const input = pickerRef.current?.querySelector(selector);
                if (input) {
                    return input;
                }
            }

            return null;
        };

        const attachInputInterceptor = (searchInput) => {

            if (!searchInput) {
                return false;
            }

            const originalOnInput = searchInput.oninput;
            searchInput.oninput = function (e) {

                const searchTerm = e.target.value;

                if (useProfanityFilter) {
                    const { passesCheck, badPhrases } = runProfanityCheck(searchTerm);

                    if (!passesCheck || badPhrases.length > 0) {
                        e.preventDefault();
                        e.stopPropagation();

                        e.target.value = '';

                        setBadWordList(badPhrases);
                        setBadWordsDialogOpen(true);
                        return false;
                    }
                }

                if (originalOnInput) {
                    return originalOnInput.call(this, e);
                }
            };

            searchInput.addEventListener('input', (e) => {

                const searchTerm = e.target.value;

                if (useProfanityFilter) {
                    const { passesCheck, badPhrases } = runProfanityCheck(searchTerm);

                    if (!passesCheck || badPhrases.length > 0) {
                        setTimeout(() => {

                            searchInput.value = '';
                            setBadWordList(badPhrases);
                            setBadWordsDialogOpen(true);
                        }, 0);
                    }
                }
            }, true);

            return true;
        };

        const observer = new MutationObserver(() => {

            const searchInput = findSearchInput();
            if (searchInput) {
                observer.disconnect();

                attachInputInterceptor(searchInput);
            }
        });

        const immediateInput = findSearchInput();
        if (immediateInput) {
            attachInputInterceptor(immediateInput);
        }
        else {
            if (pickerRef.current) {
                observer.observe(pickerRef.current, {
                    childList: true,
                    subtree: true
                });

                setTimeout(() => {

                    const delayedInput = findSearchInput();
                    if (delayedInput) {
                        observer.disconnect();
                        attachInputInterceptor(delayedInput);
                    }
                }, 500);
            }
        }

        return () => {

            observer.disconnect();
        };
    }, [
        pickerRef,
        runProfanityCheck,
        useProfanityFilter,
        setBadWordsDialogOpen,
        setBadWordList
    ]);

    return (
        <GifPickerWrapper ref={pickerRef}>
            <GifPicker
                provider={provider}
                onGifClick={onGifClick}
                {...otherProps}
            />
        </GifPickerWrapper>
    );
};

internals.SafeGifPicker.propTypes = {
    onGifClick: T.func.isRequired,
    useProfanityFilter: T.bool,
    setBadWordsDialogOpen: T.func.isRequired,
    setBadWordList: T.func.isRequired,
    width: T.oneOfType([T.string, T.number]),
    height: T.oneOfType([T.string, T.number]),
    contentFilter: T.string
};

KlipyGifPicker.propTypes = {
    onGifClick: T.func.isRequired,
    useProfanityFilter: T.bool,
    setBadWordsDialogOpen: T.func.isRequired,
    setBadWordList: T.func.isRequired,
    width: T.oneOfType([T.string, T.number]),
    height: T.oneOfType([T.string, T.number]),
    contentFilter: T.string
};
